import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { MapPin, Package, Utensils, TrendingUp, Shield, Building, Heart, Users, ArrowRight, CheckCircle, Loader2, Zap, Activity, Fingerprint, UserCheck } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WCDProgramsSection = () => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("child-nutrition");

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/wcd-programs/`);
      setPrograms(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching WCD programs:', error);
      setLoading(false);
    }
  };

  const iconMap = {
    "MapPin": MapPin,
    "Package": Package,
    "Utensils": Utensils,
    "TrendingUp": TrendingUp,
    "Shield": Shield,
    "Building": Building,
    "Heart": Heart,
    "Users": Users,
    "Activity": Activity,
    "Fingerprint": Fingerprint,
    "UserCheck": UserCheck
  };

  const handleProgramClick = (slug) => {
    navigate(`/wcd-programs/${slug}`);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getProgramsByCategory = (category) => {
    return programs.filter(service => service.category === category);
  };

  const tabData = {
    "field-operations": {
      title: "Field Operations & Monitoring",
      description: "Real-time tracking and monitoring of field activities, AWW attendance, and community engagement",
      programs: [...getProgramsByCategory("Monitoring & Verification")]
    },
    "nutrition-supply": {
      title: "Nutrition Supply & Management", 
      description: "THR distribution, meal optimization, and nutrition supply chain management",
      programs: [...getProgramsByCategory("Nutrition & Supply Chain"), ...getProgramsByCategory("Nutrition Programs")]
    },
    "child-development": {
      title: "Child Development & Health",
      description: "Growth monitoring, health tracking, behavior change, and development assessment systems",
      programs: [...getProgramsByCategory("Health & Nutrition"), ...getProgramsByCategory("Adolescent Health")]
    },
    "infrastructure": {
      title: "Infrastructure & Digital Systems",
      description: "AWC infrastructure, digital authentication, and beneficiary enrollment management",
      programs: [...getProgramsByCategory("Infrastructure Development"), ...getProgramsByCategory("Digital Authentication"), ...getProgramsByCategory("Lifecycle Management")]
    }
  };

  if (loading) {
    return (
      <section id="ai-programs" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-pink-500" />
            <p className="text-gray-600">Loading WCD AI programs...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="ai-programs" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="bg-pink-100 text-pink-700 px-4 py-2 text-sm mb-4">
            WCD AI Initiatives
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            AI-Driven Women & Child Development
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Advanced AI solutions empowering women and ensuring child development across Gujarat's WCD programs
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {programs.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card 
                key={service.id} 
                className="relative group hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
                onClick={() => handleProgramClick(service.slug)}
              >
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    {IconComponent ? <IconComponent className="w-8 h-8 text-white" /> : <span className="text-2xl">🔧</span>}
                  </div>
                  <CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-pink-600 transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {service.description}
                  </p>
                  <Badge variant="secondary" className="bg-green-100 text-green-700 mb-4">
                    {service.impact}
                  </Badge>
                  
                  {/* Quick Stats - Real Data from Overview */}
                  {service.overview && service.overview.key_metrics && service.overview.key_metrics.length > 0 && (
                    <div className="text-xs text-gray-500 space-y-2 mt-4">
                      {service.overview.key_metrics.slice(0, 3).map((metric, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <span className="text-left">{metric.label}:</span>
                          <span className="font-bold text-gray-900">{metric.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
                
                {/* Overlay for click indication */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/90 backdrop-blur-sm rounded-full p-2">
                    <ArrowRight className="w-4 h-4 text-pink-600" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Detailed Tabs Section */}
        <div className="bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Explore AI Solutions in Detail
          </h3>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-white p-1 rounded-xl shadow-sm">
              <TabsTrigger value="field-operations" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span className="hidden sm:inline">Field Ops</span>
              </TabsTrigger>
              <TabsTrigger value="nutrition-supply" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                <span className="hidden sm:inline">Nutrition</span>
              </TabsTrigger>
              <TabsTrigger value="child-development" className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Development</span>
              </TabsTrigger>
              <TabsTrigger value="infrastructure" className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                <span className="hidden sm:inline">Infrastructure</span>
              </TabsTrigger>
            </TabsList>
            
            {Object.entries(tabData).map(([key, data]) => (
              <TabsContent key={key} value={key} className="mt-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-4">
                      {data.title}
                    </h4>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {data.description}
                    </p>
                    
                    {data.programs.length > 0 && (
                      <>
                        <h5 className="font-semibold text-gray-900 mb-4">Available Programs:</h5>
                        <ul className="space-y-3 mb-6">
                          {data.programs.map((service) => (
                            <li key={service.id} className="flex items-center text-gray-600">
                              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                              <button 
                                onClick={() => handleProgramClick(service.slug)}
                                className="text-left hover:text-pink-600 transition-colors underline"
                              >
                                {service.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    
                    <Button 
                      className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                      onClick={() => {
                        if (data.programs.length > 0) {
                          handleProgramClick(data.programs[0].slug);
                        }
                      }}
                    >
                      Explore Programs
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h5 className="font-semibold text-gray-900 mb-4">Category Overview</h5>
                    {data.programs.length > 0 ? (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Programs</span>
                          <span className="font-bold text-lg text-gray-900">{data.programs.length}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Active Programs</span>
                          <span className="font-bold text-lg text-gray-900">
                            {data.programs.filter(p => p.is_active).length}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Categories Covered</span>
                          <span className="font-bold text-lg text-gray-900">
                            {[...new Set(data.programs.map(p => p.category))].length}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Key Features</span>
                          <span className="font-bold text-lg text-gray-900">
                            {data.programs.reduce((sum, p) => sum + (p.features ? p.features.length : 0), 0)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">
                        No programs available in this category yet.
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default WCDProgramsSection;